import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { NgIconsModule } from "@ng-icons/core";
import { TagPreset, TagSeverity, TagVariant } from "src/app/shared/constants/tag-presets";
import { isNgIconName } from "../utils/icon";

/**
 * EVA Tag - Badge/chip pour statuts et catégories
 *
 * Composant 100% custom utilisant les tokens EVA.
 * Supporte variantes soft/filled/outlined, sévérités et mode removable.
 *
 * @example
 * // Tag simple
 * <eva-tag label="Actif" severity="success"></eva-tag>
 *
 * // Tag avec preset (recommandé pour les statuts)
 * <eva-tag [preset]="CUSTOMER_TYPE_TAGS['COMPANY']"></eva-tag>
 *
 * // Tag avec icône PrimeIcon
 * <eva-tag label="En attente" severity="warning" icon="pi pi-clock"></eva-tag>
 *
 * // Tag avec HeroIcon — l'icône doit être fournie par le composant parent
 * // via provideIcons({ heroClock }), comme pour eva-button
 * <eva-tag label="En attente" severity="warning" icon="heroClock"></eva-tag>
 *
 * // Tag removable
 * <eva-tag label="Angular" [removable]="true" (onRemove)="removeTag()"></eva-tag>
 */
@Component({
  selector: "eva-tag",
  standalone: true,
  imports: [NgIconsModule],
  templateUrl: "./eva-tag.component.html",
  styleUrls: ["./eva-tag.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { "[class.eva-tag-host--truncate]": "truncate" },
})
export class EvaTagComponent {
  // ==========================================================================
  // Inputs
  // ==========================================================================

  /**
   * Preset de tag — applique automatiquement label, severity, icon et variant.
   * Les inputs individuels (label, severity, etc.) peuvent override le preset.
   */
  @Input() preset: TagPreset | null = null;

  /** Texte du tag */
  @Input() label: string = "";

  /** Couleur/sévérité du tag */
  @Input() severity: TagSeverity = "primary";

  /** Taille du tag */
  @Input() size: "small" | "medium" | "large" = "medium";

  /** Variante de style */
  @Input() variant: TagVariant = "soft";

  /**
   * Icône à gauche. Deux familles acceptées :
   * - PrimeIcon : classe CSS, ex. "pi pi-check"
   * - ng-icon : nom d'icône, ex. "heroClock" / "evaHistory" (dans APP_ICONS ou fourni
   *   par le parent via `provideIcons`)
   */
  @Input() icon: string = "";

  /** Affiche un bouton de suppression */
  @Input() removable: boolean = false;

  /** Forme arrondie (pill) */
  @Input() rounded: boolean = false;

  /** Couleur custom (hex) — override severity quand défini */
  @Input() color: string = "";

  /** Libellé sur 2 lignes max puis « … » quand le conteneur est trop étroit (le bouton × reste visible) */
  @Input() truncate: boolean = false;

  // ==========================================================================
  // Outputs
  // ==========================================================================

  /** Émis quand le bouton de suppression est cliqué (mode removable) */
  @Output() onRemove = new EventEmitter<void>();

  // ==========================================================================
  // Resolved values (preset → input fallback)
  // ==========================================================================

  get _label(): string {
    return this.label || this.preset?.label || "";
  }

  get _severity(): TagSeverity {
    // Si label est fourni directement (pas via preset), utiliser severity directe
    if (this.label) return this.severity;
    return this.preset?.severity ?? this.severity;
  }

  get _icon(): string {
    if (this.icon) return this.icon;
    return this.preset?.icon ?? "";
  }

  /** ng-icons se rendent via <ng-icon name>, les PrimeIcons via <i class> */
  get isHeroIcon(): boolean {
    return isNgIconName(this._icon);
  }

  get _variant(): TagVariant {
    // Si variant est explicitement mis par l'utilisateur (pas la valeur par défaut) on le garde
    if (this.label) return this.variant;
    return this.preset?.variant ?? this.variant;
  }

  // ==========================================================================
  // Computed
  // ==========================================================================

  /** Background rgba 18% pour le mode soft/custom — assez présent pour délimiter sans saturer */
  private get colorTint(): string {
    if (!this.color) return "";
    const hex = this.color.replace("#", "");
    const parse = (h: string) => parseInt(h, 16);
    if (hex.length === 6) {
      return `rgba(${parse(hex.substring(0, 2))}, ${parse(hex.substring(2, 4))}, ${parse(hex.substring(4, 6))}, 0.18)`;
    }
    if (hex.length === 3) {
      return `rgba(${parse(hex[0] + hex[0])}, ${parse(hex[1] + hex[1])}, ${parse(hex[2] + hex[2])}, 0.18)`;
    }
    return this.color;
  }

  /**
   * Version assombrie de this.color pour le texte en variante soft.
   * Conserve la teinte (HSL) et clamp la lightness à 32 % max.
   * Garantit un contraste lisible même pour les pastels (mint, sable, gris…).
   */
  private get colorDarkenedForText(): string {
    if (!this.color) return "";
    const cleaned = this.color.replace("#", "");
    const expand =
      cleaned.length === 3
        ? cleaned
            .split("")
            .map((c) => c + c)
            .join("")
        : cleaned;
    if (expand.length !== 6) return this.color;

    const r = parseInt(expand.substring(0, 2), 16) / 255;
    const g = parseInt(expand.substring(2, 4), 16) / 255;
    const b = parseInt(expand.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
          break;
        case g:
          h = ((b - r) / d + 2) / 6;
          break;
        case b:
          h = ((r - g) / d + 4) / 6;
          break;
      }
    }

    const newL = Math.min(l, 0.32);
    const hueToRgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 0.5) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = newL < 0.5 ? newL * (1 + s) : newL + s - newL * s;
    const p = 2 * newL - q;
    const nr = Math.round(hueToRgb(p, q, h + 1 / 3) * 255);
    const ng = Math.round(hueToRgb(p, q, h) * 255);
    const nb = Math.round(hueToRgb(p, q, h - 1 / 3) * 255);
    return `rgb(${nr}, ${ng}, ${nb})`;
  }

  get customBgColor(): string | null {
    if (!this.color) return null;
    if (this._variant === "filled") return this.color;
    if (this._variant === "outlined") return "transparent";
    return this.colorTint; // soft (default)
  }

  get customTextColor(): string | null {
    if (!this.color) return null;
    if (this._variant === "filled") return "var(--eva-text-primary-invert)";
    // soft : version assombrie de la couleur (HSL L≤32%) — garde l'identité couleur
    // mais reste lisible même pour les pastels
    if (this._variant === "soft") return this.colorDarkenedForText;
    return this.color; // outlined : texte coloré brut (pas de fond)
  }

  get customBorderColor(): string | null {
    if (!this.color) return null;
    if (this._variant === "filled") return "transparent";
    return this.color;
  }

  // ==========================================================================
  // Methods
  // ==========================================================================

  handleRemove(event: MouseEvent): void {
    event.stopPropagation();
    this.onRemove.emit();
  }
}
